import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Mail, Phone, MapPin, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';

const WHATSAPP_NUMBER = '+34678423475';
const EMAIL = 'carloscasallopez@gmail.com';

const Contact = () => {
  const { t } = useLanguage();

  const openWhatsApp = () => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}`, '_blank');
  };

  const openEmail = () => {
    window.location.href = `mailto:${EMAIL}`;
  };

  return (
    <section 
      className="py-24 md:py-32 bg-white"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#C2410C] mb-4 font-semibold">
            Contacto
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#1C1917] mb-4">
            {t.contact.title}
          </h2>
          <p className="text-lg text-[#57534E]">
            {t.contact.subtitle}
          </p>
        </motion.div>

        {/* Contact Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {/* WhatsApp */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-[#FDFBF7] rounded-2xl p-8 text-center"
            data-testid="contact-whatsapp"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#25D366] flex items-center justify-center text-white">
              <MessageCircle className="h-8 w-8" />
            </div>
            <h3 className="font-display text-xl font-bold text-[#1C1917] mb-2">
              {t.contact.whatsapp}
            </h3>
            <p className="text-[#57534E] mb-4">{WHATSAPP_NUMBER}</p>
            <Button
              onClick={openWhatsApp}
              className="bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-full whatsapp-btn"
              data-testid="whatsapp-button"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Abrir WhatsApp
            </Button>
          </motion.div>

          {/* Email */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-[#FDFBF7] rounded-2xl p-8 text-center"
            data-testid="contact-email"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#C2410C] flex items-center justify-center text-white">
              <Mail className="h-8 w-8" />
            </div>
            <h3 className="font-display text-xl font-bold text-[#1C1917] mb-2">
              {t.contact.email}
            </h3>
            <p className="text-[#57534E] mb-4">{EMAIL}</p>
            <Button
              onClick={openEmail}
              variant="outline"
              className="border-[#C2410C] text-[#C2410C] hover:bg-[#C2410C]/10 rounded-full"
              data-testid="email-button"
            >
              <Mail className="h-4 w-4 mr-2" />
              Enviar Email
            </Button>
          </motion.div>

          {/* Phone */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="bg-[#FDFBF7] rounded-2xl p-8 text-center"
            data-testid="contact-phone"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#F59E0B] flex items-center justify-center text-white">
              <Phone className="h-8 w-8" />
            </div>
            <h3 className="font-display text-xl font-bold text-[#1C1917] mb-2">
              {t.contact.phone}
            </h3>
            <p className="text-[#57534E] mb-4">{WHATSAPP_NUMBER}</p>
            <Button
              onClick={() => window.location.href = `tel:${WHATSAPP_NUMBER}`}
              variant="outline"
              className="border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10 rounded-full"
              data-testid="phone-button"
            >
              <Phone className="h-4 w-4 mr-2" />
              Llamar
            </Button>
          </motion.div>
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto"
        >
          {/* Location */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#C2410C]/10 flex items-center justify-center flex-shrink-0">
              <MapPin className="h-6 w-6 text-[#C2410C]" />
            </div>
            <div>
              <h4 className="font-semibold text-[#1C1917] mb-1">Ubicación</h4>
              <p className="text-[#57534E] text-sm">
                Centro de Madrid. Recogida en hotel o puntos de encuentro.
              </p>
            </div>
          </div>

          {/* Schedule */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#F59E0B]/10 flex items-center justify-center flex-shrink-0">
              <Clock className="h-6 w-6 text-[#F59E0B]" />
            </div>
            <div>
              <h4 className="font-semibold text-[#1C1917] mb-1">Horarios</h4>
              <p className="text-[#57534E] text-sm">
                Verano: 10:00 - 22:00<br />
                Invierno: 10:00 - 19:00
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
